import * as Sentry from '@sentry/vue'
import type { Session } from '@supabase/supabase-js'
import { AuthError, isAuthRetryableFetchError } from '@supabase/supabase-js'
import { createSingletonPromise } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, ref, watch, watchEffect } from 'vue'
import { useRouter } from 'vue-router'

import { captureException } from '@/sentry'
import { useRealtimeStore } from '@/stores/realtime-store'
import supabase from '@/supabase'
import { arraysEqual } from '@/utils'

export const useAuthStore = defineStore('auth', () => {
  const session = ref<Session | null>(null)
  const realtimeStore = useRealtimeStore()
  const router = useRouter()

  const isAdmin = computed(() => userRoles.value?.includes('admin'))
  const isLoggedIn = computed(() => userId.value !== null && userRoles.value !== null)
  const userId = computed(() => session.value?.user.id ?? null)
  const email = computed(() => session.value?.user.email ?? null)
  const companyId = computed<string | null>(() => session.value?.user.app_metadata?.company_id ?? null)
  const userRoles = computed(() => {
    const roles: Role[] = []
    const jwtRoles: string[] = session.value?.user.app_metadata?.user_roles ?? []
    if (jwtRoles.includes('admin')) roles.push('admin')
    if (companyId.value) roles.push('user')
    return roles
  })
  const firstName = computed<string | null>(() => session.value?.user.user_metadata?.first_name ?? null)
  const lastName = computed<string | null>(() => session.value?.user.user_metadata?.last_name ?? null)
  const avatarImageId = computed<string | null>(() => session.value?.user.user_metadata?.avatar_image_id ?? null)
  const name = computed(() => [firstName.value, lastName.value].filter(Boolean).join(' ') || null)

  const hasRole = (requiredRole: Role): boolean => {
    if (isAdmin.value) return true
    return userRoles.value.includes(requiredRole)
  }

  const isUser = computed(() => isLoggedIn.value && hasRole('user') && companyId.value !== null)

  watchEffect(() => {
    if (!userId.value && !email.value) {
      Sentry.setUser(null)
    } else {
      Sentry.setUser({
        id: userId.value ?? undefined,
        email: email.value ?? undefined
      })
    }
  })

  watch(userRoles, async (newUserRoles, oldUserRoles) => {
    if (!session.value?.user) return
    if (arraysEqual(newUserRoles, oldUserRoles)) return
    const retryForbiddenRoute = router.currentRoute.value.path === '/forbidden' && router.currentRoute.value.query['redirect']
    if (retryForbiddenRoute) {
      const redirectParam = router.currentRoute.value.query['redirect'] as string
      await router.push(redirectParam)
    } else {
      const requiredRole = router.currentRoute.value.meta.requiresRole
      if (requiredRole && !hasRole(requiredRole)) {
        await router.push({ path: '/forbidden', query: { requiredRole, redirect: router.currentRoute.value.fullPath }, replace: true })
      }
    }
  })

  watch(() => session.value?.access_token, async (newToken) => {
    await supabase.realtime.setAuth(newToken ?? undefined)
  })

  // Central handler for session invalidation from any source (logout, self-deletion, admin deletion)
  watch(() => session.value, async (newSession, oldSession) => {
    if (oldSession && !newSession) {
      await realtimeStore.unsubscribeAll()
      await router.push('/')
    }
  })

  // Singleton deduplicates concurrent calls; reset() after settling ensures future calls re-execute
  const _refreshUserSession = createSingletonPromise(async () => {
    try {
      const { data, error } = await supabase.auth.refreshSession()
      if (error) throw error
      session.value = data.session ?? null
    } catch (error: unknown) {
      captureException(error)
      // Non-retryable failure means the session is dead — sign out to clear storage,
      // otherwise authGuard re-reads the preserved session and silently re-authenticates.
      if (!isAuthRetryableFetchError(error)) {
        await logout()
      }
    } finally {
      await _refreshUserSession.reset()
    }
  })

  const refreshUserSession = (): Promise<void> => _refreshUserSession()

  async function logout(): Promise<void> {
    try {
      await supabase.auth.signOut()
    } finally {
      session.value = null
    }
  }

  async function login(email: string, password: string): Promise<void> {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
  }

  async function signup(email: string, password: string, firstName: string, lastName: string, companyName: string): Promise<void> {
    const metadata: { first_name: string, last_name: string, company_name: string } = {
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      company_name: companyName.trim()
    }

    const { data, error } = await supabase.auth.signUp({
      email: email.trim(),
      password: password,
      options: {
        emailRedirectTo: `${window.location.origin}/confirm-email/success`,
        data: metadata
      }
    })
    if (error) throw error
    // Supabase handles duplicate emails differently depending on the email confirmation setting:
    // - Confirmations OFF (local): signUp() throws AuthError with code 'user_already_exists'
    // - Confirmations ON (production): signUp() returns a fake user with empty identities (anti-enumeration)
    if (data.user && data.user.identities?.length === 0) {
      throw new AuthError('User already registered', 400, 'user_already_exists')
    }
  }

  async function requestPasswordReset(email: string): Promise<void> {
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: `${window.location.origin}/reset-password` })
    if (error) throw error
  }

  async function changePassword(password: string): Promise<void> {
    const { error } = await supabase.auth.updateUser({ password })
    if (error) throw error
  }

  async function changeEmail(email: string): Promise<void> {
    const { error } = await supabase.auth.updateUser({ email }, { emailRedirectTo: `${window.location.origin}/change-email/success` })
    if (error) throw error
  }

  async function initAuthStore(): Promise<void> {
    const { data, error } = await supabase.auth.getSession()
    if (error) throw error
    if (!data.session?.user) throw new Error('User not logged in')
    session.value = data.session
    supabase.auth.onAuthStateChange(async (_, newSession) => {
      session.value = newSession
    })
  }

  async function resendConfirmationEmail(email: string): Promise<void> {
    const { error } = await supabase.auth.resend({ type: 'signup', email })
    if (error) throw error
  }

  async function verifyPassword(password: string): Promise<boolean> {
    const { data, error } = await supabase.rpc('verify_user_password', { password })
    if (error) throw error
    return data as boolean
  }

  async function deleteOwnAccount(password: string): Promise<void> {
    const { error } = await supabase.rpc('delete_own_account', { password })
    if (error) throw error
    await logout()
  }

  return {
    userId,
    email,
    isAdmin,
    isLoggedIn,
    companyId,
    isUser,
    avatarImageId,
    name,
    userRoles,
    hasRole,
    logout,
    login,
    signup,
    requestPasswordReset,
    initAuthStore,
    resendConfirmationEmail,
    changePassword,
    changeEmail,
    verifyPassword,
    deleteOwnAccount,
    refreshUserSession
  }
})
